import {
  ActionIcon,
  Box,
  Group,
  LoadingOverlay,
  Paper,
  Text,
} from "@mantine/core";
import { IconCheck, IconPencil, IconX } from "@tabler/icons-react";
import RichTextEditor, {
  RichTextEditorRef,
} from "components/elements/editor/RichTextEditor";
import { useSessionStore } from "stores/userSessionStore";

import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
} from "common/constants";
import { getPage, updatePage } from "common/page.service";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SafeContent } from "utils/reactHelper";

interface CmsPageProps {
  pageTitle: string;
  showBorder?: boolean; // Optional prop to control border visibility
}

export const CmsPage = ({ pageTitle, showBorder }: CmsPageProps) => {
  const navigate = useNavigate();
  const editorRef = useRef<RichTextEditorRef>(null);
  const { isAdmin } = useSessionStore();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState("");

  useEffect(() => {
    if (!pageTitle) return;
    const fetchPage = async () => {
      try {
        const resp = await safeApiCall(getPage(pageTitle));
        setContent(resp?.data?.[0]?.HtmlMarkup ?? "");
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [pageTitle]);

  //
  const handleCancel = () => {
    editorRef.current?.reset();
    setEditing(false);
    navigate(0); // Redirect to the CMS page
  };
  /**
   *
   */
  const handleSave = () => {
    const updatedHTML = editorRef.current?.getHTML() || "";
    setContent(updatedHTML);
    setEditing(false);

    safeApiCall(
      updatePage(pageTitle, updatedHTML).then((res) => {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
          color: MantineNotificationColor.Success,
          position: MantineNotificationPosition.TopCenter,
        });
      }),
    );
    navigate(0); // Redirect to the CMS page
  };

  return (
    <Box pos="relative" style={{ minHeight: "100px", margin: "20px 0" }}>
      <LoadingOverlay
        visible={loading}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />

      <Paper
        {...(showBorder && {
          radius: "md",
          shadow: "sm",
          p: "md",
          withBorder: true,
        })}
      >
        {editing ? (
          <>
            <RichTextEditor
              ref={editorRef}
              content={content}
              onChange={(html) => setContent(html)}
              placeholder="Edit CMS content..."
              variant="full"
            />

            <Group mt="sm">
              <ActionIcon color="green" onClick={handleSave}>
                <IconCheck />
              </ActionIcon>
              <ActionIcon color="red" onClick={handleCancel}>
                <IconX />
              </ActionIcon>
            </Group>
          </>
        ) : (
          <>
            {isAdmin && !editing && (
              <ActionIcon
                onClick={() => setEditing(true)}
                variant="light"
                color="blue"
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 10,
                }}
              >
                <IconPencil />
              </ActionIcon>
            )}

            <div>
              {content ? (
                <SafeContent html={content} />
              ) : (
                <Text color="dimmed">No content available.</Text>
              )}
            </div>
          </>
        )}
      </Paper>
    </Box>
  );
};
